import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../utils/api';

const BLOCK_NAMES = {
  tech: '🔧 Тех. работы', design: '🎨 Дизайн', video: '🎬 Видео',
  loyalty: '🎁 Лояльность', social: '📱 Контент',
};

const STATUS_LABELS = { new: 'Новая', in_progress: 'В работе', closed: 'Закрыта' };
const PRIORITY_EMOJI = { urgent: '🔴', normal: '🟡', low: '🟢' };

function ticketPoints(ticket) {
  if (Array.isArray(ticket?.points) && ticket.points.length) return ticket.points;
  return ticket?.point ? [ticket.point] : [];
}

function ticketPointLabel(ticket) {
  const points = ticketPoints(ticket);
  if (points.length > 1) return `${points[0]} +${points.length - 1}`;
  return points[0] || '—';
}

function ticketAccent(network) {
  if (network === 'hookah') return 'ticket-accent-hookah';
  if (network?.startsWith('tabakov')) return 'ticket-accent-tabakov';
  return '';
}

export default function Tickets() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [tickets, setTickets] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [filters, setFilters] = useState({
    search: '',
    status: searchParams.get('status') || '',
    block: '',
    priority: searchParams.get('priority') || '',
    network: '',
    date_from: '',
    date_to: '',
  });
  const [offset, setOffset] = useState(0);

  // Обновляем фильтры при смене URL-параметров (навигация с той же страницы)
  useEffect(() => {
    const s = searchParams.get('status') || '';
    const p = searchParams.get('priority') || '';
    setFilters(prev => ({ ...prev, status: s, priority: p }));
    setOffset(0);
  }, [searchParams]);
  const LIMIT = 30;

  const setFilter = (key, val) => {
    setFilters(p => ({ ...p, [key]: val }));
    setOffset(0);
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { limit: LIMIT, offset };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const res = await api.get('/api/admin/tickets', { params });
      setTickets(res.data.items);
      setTotal(res.data.total);
    } catch {
      setError('Ошибка загрузки заявок');
    } finally {
      setLoading(false);
    }
  }, [filters, offset]);

  useEffect(() => { load(); }, [load]);

  const resetFilters = () => {
    setFilters({ search: '', status: '', block: '', priority: '', network: '', date_from: '', date_to: '' });
    setOffset(0);
  };

  return (
    <div className="page">
      <div className="page-header">
        <h1>Заявки <span className="badge">{total}</span></h1>
      </div>

      <div className="filters-bar">
        <input
          className="filter-search"
          placeholder="Поиск по номеру или описанию..."
          value={filters.search}
          onChange={e => setFilter('search', e.target.value)}
        />
        <select value={filters.status} onChange={e => setFilter('status', e.target.value)}>
          <option value="">Все статусы</option>
          <option value="new">Новая</option>
          <option value="in_progress">В работе</option>
          <option value="closed">Закрыта</option>
        </select>
        <select value={filters.block} onChange={e => setFilter('block', e.target.value)}>
          <option value="">Все категории</option>
          {Object.entries(BLOCK_NAMES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select value={filters.priority} onChange={e => setFilter('priority', e.target.value)}>
          <option value="">Все приоритеты</option>
          <option value="urgent">🔴 Срочно</option>
          <option value="normal">🟡 Обычно</option>
          <option value="low">🟢 Не горит</option>
        </select>
        <input type="date" value={filters.date_from} onChange={e => setFilter('date_from', e.target.value)} title="От" />
        <input type="date" value={filters.date_to} onChange={e => setFilter('date_to', e.target.value)} title="До" />
        <button className="btn-ghost" onClick={resetFilters}>Сбросить</button>
      </div>

      {error && <div className="error-msg">{error}</div>}

      {loading ? (
        <div className="page-loader">Загрузка...</div>
      ) : tickets.length === 0 ? (
        <div className="empty-state">Заявок не найдено</div>
      ) : (
        <>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Номер</th>
                  <th>Категория</th>
                  <th>Точка</th>
                  <th>Сотрудник</th>
                  <th>Приоритет</th>
                  <th>Статус</th>
                  <th>Дата</th>
                </tr>
              </thead>
              <tbody>
                {tickets.map(t => (
                  <tr key={t.id} className={`table-row clickable ticket-row-accent ${ticketAccent(t.network)}`} onClick={() => navigate(`/admin/tickets/${t.id}`)}>
                    <td className="mono">{t.ticket_number}</td>
                    <td>{BLOCK_NAMES[t.block] || t.block}</td>
                    <td>{ticketPointLabel(t)}</td>
                    <td>{t.user?.full_name || '—'}</td>
                    <td>{PRIORITY_EMOJI[t.priority] || '—'} {t.priority === 'urgent' ? 'Срочно' : t.priority === 'low' ? 'Не горит' : 'Обычно'}</td>
                    <td><span className={`status-badge status--${t.status}`}>{STATUS_LABELS[t.status] || t.status}</span></td>
                    <td>{new Date(t.created_at).toLocaleDateString('ru-RU')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="pagination">
            <button className="btn-secondary" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - LIMIT))}>← Пред</button>
            <span>{offset + 1}–{Math.min(offset + LIMIT, total)} из {total}</span>
            <button className="btn-secondary" disabled={offset + LIMIT >= total} onClick={() => setOffset(offset + LIMIT)}>След →</button>
          </div>
        </>
      )}
    </div>
  );
}
